// Screen: "Game Viewer" — Watch AI-only showcase games
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AI_NAMES } from '../config/aiConfig';

function useIsMobile() {
  const [mobile, setMobile] = React.useState(window.innerWidth < 600);
  React.useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export default function GameViewerPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [playerCount, setPlayerCount] = useState(3);
  const [selectedPlayers, setSelectedPlayers] = useState([]);

  const togglePlayer = (name) => {
    setSelectedPlayers(prev => {
      if (prev.includes(name)) return prev.filter(n => n !== name);
      const maxAI = playerCount; // all seats are AI in showcase
      if (prev.length >= maxAI) return prev;
      return [...prev, name];
    });
  };

  const handlePlayerCountChange = (count) => {
    setPlayerCount(count);
    // Trim selection if switching from 4P to 3P
    setSelectedPlayers(prev => prev.slice(0, count));
  };

  const startShowcase = () => {
    // Fill remaining slots with random names if not enough selected
    let players = [...selectedPlayers];
    const available = AI_NAMES.filter(n => !players.includes(n));
    while (players.length < playerCount) {
      const pick = available.splice(Math.floor(Math.random() * available.length), 1)[0];
      if (pick) players.push(pick);
    }

    const params = new URLSearchParams({
      players: String(playerCount),
      mode: 'free',
      showcase: 'true',
      aiNames: JSON.stringify(players),
    });
    navigate(`/play?${params.toString()}`);
  };

  return (
    <div style={{ fontFamily: "'Georgia', serif", color: '#e2e8f0' }}>
      <h1 style={{ fontSize: isMobile ? 22 : 32, fontWeight: 700, marginBottom: 8 }}>
        Game Viewer
      </h1>
      <p style={{ color: '#8b949e', fontSize: 14, marginBottom: 24 }}>
        Watch AI players compete against each other. Pick your players and start the game!
      </p>

      {/* Player count toggle */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 8 }}>Number of Players</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {[3, 4].map(count => (
            <button
              key={count}
              onClick={() => handlePlayerCountChange(count)}
              style={{
                padding: '10px 28px',
                borderRadius: 8,
                border: playerCount === count ? '2px solid #58a6ff' : '2px solid #30363d',
                background: playerCount === count ? 'rgba(88,166,255,0.15)' : '#161b22',
                color: playerCount === count ? '#58a6ff' : '#8b949e',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {count} Players
            </button>
          ))}
        </div>
      </div>

      {/* AI Player selection */}
      <div style={{
        background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
        padding: isMobile ? 16 : 24, marginBottom: 24
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#58a6ff', marginBottom: 4 }}>
          Choose AI Players
        </div>
        <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 16 }}>
          Select up to {playerCount} players, or leave empty for random selection.
          ({selectedPlayers.length}/{playerCount} selected)
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: 10,
        }}>
          {AI_NAMES.map(name => {
            const selected = selectedPlayers.includes(name);
            const disabled = !selected && selectedPlayers.length >= playerCount;
            return (
              <button
                key={name}
                onClick={() => !disabled && togglePlayer(name)}
                style={{
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: selected ? '2px solid #4ade80' : '2px solid #30363d',
                  background: selected ? 'rgba(74,222,128,0.1)' : disabled ? '#0d1117' : '#161b22',
                  color: selected ? '#4ade80' : disabled ? '#484f58' : '#c9d1d9',
                  fontSize: 14,
                  fontWeight: selected ? 700 : 400,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  fontFamily: "'Georgia', serif",
                }}
              >
                {selected && '✓ '}{name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected players preview */}
      {selectedPlayers.length > 0 && (
        <div style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
          padding: 16, marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{ color: '#8b949e', fontSize: 12 }}>Lineup:</span>
          {selectedPlayers.map((name, i) => (
            <span key={name} style={{
              background: '#1e293b', borderRadius: 6, padding: '4px 12px',
              color: '#e2e8f0', fontSize: 13, fontWeight: 600,
              border: '1px solid #334155'
            }}>
              {name}
              {i < selectedPlayers.length - 1 ? '' : ''}
            </span>
          ))}
          {selectedPlayers.length < playerCount && (
            <span style={{ color: '#484f58', fontSize: 12, fontStyle: 'italic' }}>
              + {playerCount - selectedPlayers.length} random
            </span>
          )}
        </div>
      )}

      {/* Start button */}
      <button
        onClick={startShowcase}
        style={{
          background: 'linear-gradient(135deg, #238636 0%, #2ea043 100%)',
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          padding: '14px 36px',
          fontSize: 16,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: "'Georgia', serif",
          boxShadow: '0 4px 14px rgba(35,134,54,0.4)',
          transition: 'transform 0.15s',
        }}
        onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
        onMouseLeave={e => e.target.style.transform = 'scale(1)'}
      >
        Start {playerCount}-Player Showcase
      </button>

      <div style={{ marginTop: 16, fontSize: 12, color: '#484f58' }}>
        You'll watch the AI players compete in a full Buck Euchre game.
      </div>
    </div>
  );
}
